import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { SpinDirection } from "../../shared/base-components/spinner-buttons.component";

import qcompare from "qcompare";

export const INPUTMASK_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxTimeEditComponent),
  multi: true,
};

@Component({
  selector: "bfx-time-edit",
  templateUrl: "./html/bfx-time-edit.html",
  host: {
    "[class.ui-inputwrapper-filled]": "filled",
    "[class.ui-inputwrapper-focus]": "focus",
  },
  providers: [INPUTMASK_VALUE_ACCESSOR],
  standalone: false,
})
export class BfxTimeEditComponent implements ControlValueAccessor, AfterViewInit {
  public params: any;
  public isBuy: boolean = false;
  public editingEnabled: boolean = false;
  public title: string = null;
  public isCall: boolean = false;
  public spun: boolean = false;

  public hour: string = "HH";
  public minute: string = "MM";
  public second: string = "SS";

  @Input("disabled") disabled: boolean = false;

  @Input("autoClear") autoClear: boolean = true;

  @Input("showSeconds") showSeconds: boolean = false;

  @Input("mask") mask: string = null;

  private _spinner: any | null = null;

  public get spinner(): any | null {
    return this._spinner;
  }

  @Input("spinner")
  public set spinner(value: any | null) {
    this._spinner = value;
  }

  @ViewChild("hours", { static: true })
  public hoursElement: ElementRef;

  @ViewChild("minutes", { static: true })
  public minutesElement: ElementRef;

  @ViewChild("seconds", { static: false })
  public secondsElement: ElementRef;

  @Output("onFocus") onFocusEvent: EventEmitter<any> = new EventEmitter();

  @Output("onBlur") onBlurEvent: EventEmitter<any> = new EventEmitter();

  @Output("onInput") onInputEvent: EventEmitter<any> = new EventEmitter();

  private _value: any;

  public get value(): any {
    return this._value;
  }

  public set value(value: any) {
    if (!qcompare(this._value, value)) {
      this.writeValue(value);
    }
  }

  _mask: string;

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  input: HTMLInputElement;

  filled: boolean;

  defs: any;

  tests: any[];

  partialPosition: any;

  firstNonMaskPos: number;

  lastRequiredNonMaskPos: any;

  len: number;

  oldVal: string;

  buffer: any;

  defaultBuffer: string;

  focusText: string;

  caretTimeoutId: any;

  androidChrome: boolean;

  focused: boolean;

  lastActive: "hours" | "minutes" | "seconds" = "hours";

  constructor(
    public el: ElementRef,
    public cd: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    // here for backward compatibility with showing seconds
    if (this.mask != null && this.mask === "99:99:99") {
      this.showSeconds = true;
    }
  }

  writeValue(value: any): void {
    if (value != null) {
      if (typeof value === "string") {
        if (value.includes("::")) {
          throw new Error("Invalid Time");
        }

        value = CubiqTS.moment(value, this.getTimeFormat());
      }

      if (CubiqTS.moment.isMoment(value)) {
        let val: string = value.format(this.getTimeFormat());
        let values: string[] = val.split(":");
        this.hour = values[0];
        this.minute = values[1];
        if (this.showSeconds && values.length > 2) {
          this.second = values[2];
        }
      } else {
        this.hour = null;
        this.minute = null;
        this.second = null;
      }
    } else {
      this.hour = null;
      this.minute = null;
      this.second = null;
    }
    this._value = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  public onKeyDown(event: any): void {
    switch (event.which) {
      case 38: // Up
      case 40: // Down
        this.spun = true;
        break;
      case 37: // Left
        event.preventDefault();
        event.stopPropagation();

        if (this.showSeconds && event.target == this.secondsElement.nativeElement) {
          this.minutesElement.nativeElement.focus();
          this.minutesElement.nativeElement.select();
        }

        if (event.target == this.minutesElement.nativeElement) {
          this.hoursElement.nativeElement.focus();
          this.hoursElement.nativeElement.select();
        }
        break;
      case 39: // Right
        event.preventDefault();
        event.stopPropagation();
      case 9: // Tab
        if (event.shiftKey) {
          // no-op
        } else if (event.target == this.hoursElement.nativeElement) {
          event.preventDefault();
          event.stopPropagation();
          this.minutesElement.nativeElement.focus();
          this.minutesElement.nativeElement.select();
        } else if (this.showSeconds && event.target == this.minutesElement.nativeElement) {
          event.preventDefault();
          event.stopPropagation();
          this.secondsElement.nativeElement.focus();
          this.secondsElement.nativeElement.select();
        }
        break;
    }
  }

  public onFocus(event: any): void {
    if (event && event.target) {
      event.target.select();
    }
    this.onFocusEvent.emit(event);
  }

  public onBlur(event: FocusEvent): void {
    this.modelChange();
    this.onBlurEvent.emit(event);
  }

  public modelChange(): void {
    [this.hoursElement, this.minutesElement, this.secondsElement].forEach((element: ElementRef) => {
      if (element != null) {
        let strValue: string = element.nativeElement.value;
        strValue = strValue.trim().length == 0 ? 0 + "" : strValue;
        let value: number = parseInt(strValue, 10);
        element.nativeElement.value = value < 10 ? ("0" + value).substring(0, 2) : value;
      }
    });
    this._value = this.getValue();
    this.onModelChange(this.value);
  }

  public onInput(event: any): void {
    let value: string = event.target.value;
    let spun: boolean = this.spun;

    const trimValue = value.trim();

    if (trimValue.length > 0) {
      let num: number = parseInt(value, 10);
      let maxValue: number = parseInt(event.target.getAttribute("max"));
      if (num > maxValue || num < 0) {
        event.target.value = event.target.getAttribute("original");
      } else {
        event.target.setAttribute("original", value);

        if (trimValue.length == 2 && !spun) {
          if (event.target == this.hoursElement.nativeElement) {
            this.minutesElement.nativeElement.focus();
            this.minutesElement.nativeElement.select();
          } else if (this.showSeconds && event.target == this.minutesElement.nativeElement) {
            this.secondsElement.nativeElement.focus();
            this.secondsElement.nativeElement.select();
          }
        } else if (trimValue.length == 1 && spun) {
          event.target.value = "0" + trimValue;
        }
      }
    }
    this.spun = false;
    this.onInputEvent.emit(event);
    this.modelChange();
  }

  getValue(): any {
    this.hour = this.hoursElement.nativeElement.value;
    this.minute = this.minutesElement.nativeElement.value;

    if (this.secondsElement != null) {
      this.second = this.secondsElement.nativeElement.value;
    }

    let val: CubiqTS.Moment = CubiqTS.moment(
      `${this.hour}:${this.minute}` + (this.showSeconds ? `:${this.second}` : ""),
      this.showSeconds ? CubiqTS.moment.HTML5_FMT.TIME_SECONDS : CubiqTS.moment.HTML5_FMT.TIME,
    );
    return val.format(this.getTimeFormat());
  }

  focus() {
    setTimeout(() => {
      this.hoursElement.nativeElement.focus();
    }, 4);
  }

  blur() {
    [this.hoursElement, this.minutesElement, this.secondsElement]
      .filter((e) => e != null)
      .forEach((e) => {
        if (e.nativeElement === e.nativeElement.ownerDocument.activeElement) {
          e.nativeElement.blur();
        }
      });
  }

  private getTimeFormat() {
    return this.showSeconds ? "HH:mm:ss" : "HH:mm";
  }

  spinUp() {
    this.spinTo(SpinDirection.UP);
  }

  spinDown() {
    this.spinTo(SpinDirection.DOWN);
  }

  spinTo(direction: SpinDirection) {
    let element;

    switch (this.lastActive) {
      case "hours":
        element = this.hoursElement?.nativeElement;
        break;
      case "minutes":
        element = this.minutesElement?.nativeElement;
        break;
      case "seconds":
        element = this.secondsElement?.nativeElement;
        break;
      default:
        break;
    }
    if (element) {
      this.spun = true;

      direction == SpinDirection.UP ? element.stepUp() : element.stepDown();
      const event = {
        target: element,
      };

      this.onInput(event);

      setTimeout(() => {
        event.target.focus();
        this.onFocus(event);
      }, 0);
    }
  }
}
